// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/Communication/AlertSettings.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  FiMail,
  FiMessageSquare,
  FiClock,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiSave,
  FiX,
  FiBell,
  FiToggleLeft,
  FiToggleRight,
  FiChevronDown,
  FiChevronUp,
  FiSettings,
  FiPower,
} from 'react-icons/fi';
import api from '../../api';

const AlertSettings = ({ tenantId, onSettingsChange }) => {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    frequency: true,
    channels: true,
  });
  
  const [alertConfig, setAlertConfig] = useState({
    enabled: false,
    frequency: 'every_class',
    channels: {
      email: {
        enabled: false,
        timeWindow: {
          enabled: false,
          startTime: '10:30',
          endTime: '11:30',
        }
      },
      sms: {
        enabled: false,
        timeWindow: {
          enabled: false,
          startTime: '10:30',
          endTime: '11:30',
        }
      }
    }
  });

  const [originalConfig, setOriginalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, type: '', message: '' });

  // ============================================
  // UI HELPERS
  // ============================================
  const showAlert = useCallback((type, message) => {
    setAlertMessage({ show: true, type, message });
    setTimeout(() => {
      setAlertMessage({ show: false, type: '', message: '' });
    }, 5000);
  }, []);

  // ============================================
  // VALIDATION
  // ============================================
  const validateConfig = useCallback((config) => {
    const errors = [];
    
    if (!config.enabled) {
      setValidationErrors(errors);
      return;
    }
    
    const hasEmail = config.channels.email.enabled;
    const hasSms = config.channels.sms.enabled;
    
    if (!hasEmail && !hasSms) {
      errors.push('At least one communication channel (Email or SMS) must be enabled.');
    }
    
    if (config.frequency === 'once_a_day') {
      if (hasEmail && config.channels.email.timeWindow.enabled) {
        const { startTime, endTime } = config.channels.email.timeWindow;
        if (startTime >= endTime) {
          errors.push('Email alert start time must be before end time.');
        }
      }
      
      if (hasSms && config.channels.sms.timeWindow.enabled) {
        const { startTime, endTime } = config.channels.sms.timeWindow;
        if (startTime >= endTime) {
          errors.push('SMS alert start time must be before end time.');
        }
      }
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  }, []);

  // ============================================
  // LOAD CONFIGURATION
  // ============================================
  const fetchAlertConfig = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching alert config for tenant:', tenantId);
      
      const response = await api.get('/api/tenant/communication/absent-alert/config');
      console.log('📥 Alert config response:', response.data);
      
      if (response.data.success) {
        const config = response.data.data;
        
        // ✅ Handle both old and new channel formats
        let emailEnabled = false;
        let smsEnabled = false;
        let emailTimeWindowEnabled = false;
        let smsTimeWindowEnabled = false;
        let emailStartTime = '10:30';
        let emailEndTime = '11:30';
        let smsStartTime = '10:30';
        let smsEndTime = '11:30';
        
        // Check if channels is in old format (boolean values)
        const isOldFormat = config.channels && typeof config.channels.email === 'boolean';
        
        if (isOldFormat) {
          // Old format: { email: true, sms: true, inApp: true }
          emailEnabled = config.channels.email || false;
          smsEnabled = config.channels.sms || false;
          emailTimeWindowEnabled = config.timeWindow?.enabled || false;
          smsTimeWindowEnabled = config.timeWindow?.enabled || false;
          emailStartTime = config.timeWindow?.startTime || '10:30';
          emailEndTime = config.timeWindow?.endTime || '11:30';
          smsStartTime = config.timeWindow?.startTime || '10:30';
          smsEndTime = config.timeWindow?.endTime || '11:30';
        } else {
          // New format: { email: { enabled: true, timeWindow: {...} } }
          emailEnabled = config.channels?.email?.enabled || false;
          smsEnabled = config.channels?.sms?.enabled || false;
          emailTimeWindowEnabled = config.channels?.email?.timeWindow?.enabled || false;
          smsTimeWindowEnabled = config.channels?.sms?.timeWindow?.enabled || false;
          emailStartTime = config.channels?.email?.timeWindow?.startTime || config.timeWindow?.startTime || '10:30';
          emailEndTime = config.channels?.email?.timeWindow?.endTime || config.timeWindow?.endTime || '11:30';
          smsStartTime = config.channels?.sms?.timeWindow?.startTime || config.timeWindow?.startTime || '10:30';
          smsEndTime = config.channels?.sms?.timeWindow?.endTime || config.timeWindow?.endTime || '11:30';
        }
        
        const transformedConfig = {
          enabled: config.enabled || false,
          frequency: config.frequency || 'every_class',
          channels: {
            email: {
              enabled: emailEnabled,
              timeWindow: {
                enabled: emailTimeWindowEnabled,
                startTime: emailStartTime,
                endTime: emailEndTime,
              }
            },
            sms: {
              enabled: smsEnabled,
              timeWindow: {
                enabled: smsTimeWindowEnabled,
                startTime: smsStartTime,
                endTime: smsEndTime,
              }
            }
          }
        };
        
        setAlertConfig(transformedConfig);
        setOriginalConfig(JSON.parse(JSON.stringify(transformedConfig)));
        setHasChanges(false);
        validateConfig(transformedConfig);
      }
    } catch (error) {
      console.error('❌ Failed to fetch alert config:', error);
      showAlert('error', 'Failed to load alert settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, validateConfig, showAlert]);

  useEffect(() => {
    if (tenantId) {
      fetchAlertConfig();
    }
  }, [tenantId, fetchAlertConfig]);

  // ============================================
  // HANDLE CHANGES
  // ============================================
  const handleConfigChange = useCallback((path, value) => {
    const newConfig = { ...alertConfig };
    const keys = path.split('.');
    let current = newConfig;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    
    setAlertConfig(newConfig);
    setHasChanges(JSON.stringify(newConfig) !== JSON.stringify(originalConfig));
    validateConfig(newConfig);
  }, [alertConfig, originalConfig, validateConfig]);

  // Master toggle for the entire alert system
  const handleToggleMaster = useCallback(() => {
    const newEnabled = !alertConfig.enabled;
    handleConfigChange('enabled', newEnabled);
  }, [alertConfig.enabled, handleConfigChange]);

  // ============================================
  // MASTER TOGGLE FOR EACH FREQUENCY MODE
  // ============================================
  
  const handleToggleEveryClass = useCallback(() => {
    const isCurrentlyActive = alertConfig.frequency === 'every_class' && alertConfig.enabled;
    
    if (isCurrentlyActive) {
      setAlertConfig((prev) => ({
        ...prev,
        enabled: false,
        channels: {
          email: { ...prev.channels.email, enabled: false },
          sms: { ...prev.channels.sms, enabled: false },
        }
      }));
      setHasChanges(true);
      showAlert('info', 'Every Class alerts have been turned OFF.');
    } else {
      setAlertConfig((prev) => ({
        ...prev,
        enabled: true,
        frequency: 'every_class',
        channels: {
          email: { ...prev.channels.email, enabled: true },
          sms: { ...prev.channels.sms, enabled: true },
        }
      }));
      setHasChanges(true);
      showAlert('success', 'Every Class alerts have been turned ON. Email and SMS are now active.');
    }
  }, [alertConfig.frequency, alertConfig.enabled, showAlert]);

  const handleToggleOnceADay = useCallback(() => {
    const isCurrentlyActive = alertConfig.frequency === 'once_a_day' && alertConfig.enabled;
    
    if (isCurrentlyActive) {
      setAlertConfig((prev) => ({
        ...prev,
        enabled: false,
        channels: {
          email: { ...prev.channels.email, enabled: false },
          sms: { ...prev.channels.sms, enabled: false },
        }
      }));
      setHasChanges(true);
      showAlert('info', 'Once a Day alerts have been turned OFF.');
    } else {
      setAlertConfig((prev) => ({
        ...prev,
        enabled: true,
        frequency: 'once_a_day',
        channels: {
          email: { 
            ...prev.channels.email, 
            enabled: true,
            timeWindow: {
              enabled: true,
              startTime: prev.channels.email.timeWindow.startTime || '10:30',
              endTime: prev.channels.email.timeWindow.endTime || '11:30',
            }
          },
          sms: { 
            ...prev.channels.sms, 
            enabled: true,
            timeWindow: {
              enabled: true,
              startTime: prev.channels.sms.timeWindow.startTime || '10:30',
              endTime: prev.channels.sms.timeWindow.endTime || '11:30',
            }
          },
        }
      }));
      setHasChanges(true);
      showAlert('success', 'Once a Day alerts have been turned ON. Email and SMS with time windows are now active.');
    }
  }, [alertConfig.frequency, alertConfig.enabled, showAlert]);

  // Independent toggle for each channel
  const handleToggleChannel = useCallback((channel, value) => {
    handleConfigChange(`channels.${channel}.enabled`, value);
    
    if (value && alertConfig.frequency === 'once_a_day') {
      handleConfigChange(`channels.${channel}.timeWindow.enabled`, true);
    }
  }, [handleConfigChange, alertConfig.frequency]);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  // ============================================
  // SAVE CONFIGURATION - FIXED FOR BACKEND
  // ============================================
  const handleSave = useCallback(async () => {
    if (!validateConfig(alertConfig)) {
      showAlert('error', 'Please fix the validation errors before saving.');
      return;
    }
    
    setSaving(true);
    try {
      // ✅ FIX: Send data in the format backend expects
      const apiPayload = {
        enabled: alertConfig.enabled,
        frequency: alertConfig.frequency,
        channels: {
          email: {
            enabled: alertConfig.channels.email.enabled,
            timeWindow: {
              enabled: alertConfig.frequency === 'once_a_day' && alertConfig.channels.email.timeWindow.enabled,
              startTime: alertConfig.channels.email.timeWindow.startTime || '10:30',
              endTime: alertConfig.channels.email.timeWindow.endTime || '11:30',
            }
          },
          sms: {
            enabled: alertConfig.channels.sms.enabled,
            timeWindow: {
              enabled: alertConfig.frequency === 'once_a_day' && alertConfig.channels.sms.timeWindow.enabled,
              startTime: alertConfig.channels.sms.timeWindow.startTime || '10:30',
              endTime: alertConfig.channels.sms.timeWindow.endTime || '11:30',
            }
          },
          inApp: {
            enabled: true
          }
        },
        timeWindow: {
          enabled: alertConfig.frequency === 'once_a_day' && 
            (alertConfig.channels.email.timeWindow.enabled || alertConfig.channels.sms.timeWindow.enabled),
          startTime: alertConfig.frequency === 'once_a_day' 
            ? alertConfig.channels.email.timeWindow.startTime 
            : '10:30',
          endTime: alertConfig.frequency === 'once_a_day'
            ? alertConfig.channels.email.timeWindow.endTime
            : '11:30',
          timezone: 'Asia/Kolkata',
          outsideWindowBehavior: 'queue_for_next_day'
        }
      };
      
      console.log('📤 Saving alert config:', JSON.stringify(apiPayload, null, 2));
      
      const response = await api.put('/api/tenant/communication/absent-alert/config', apiPayload);
      console.log('📥 Save response:', response.data);
      
      if (response.data.success) {
        setOriginalConfig(JSON.parse(JSON.stringify(alertConfig)));
        setHasChanges(false);
        showAlert('success', 'Alert settings saved successfully!');
        
        if (onSettingsChange) {
          onSettingsChange(alertConfig);
        }
      } else {
        showAlert('error', response.data.message || 'Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('❌ Failed to save alert config:', error);
      console.error('Response data:', error.response?.data);
      showAlert('error', error.response?.data?.message || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [alertConfig, onSettingsChange, validateConfig, showAlert]);

  const handleReset = useCallback(() => {
    if (originalConfig) {
      setAlertConfig(JSON.parse(JSON.stringify(originalConfig)));
      setHasChanges(false);
      validateConfig(originalConfig);
      showAlert('info', 'Changes have been discarded.');
    }
  }, [originalConfig, validateConfig, showAlert]);

  // ============================================
  // FORMAT HELPERS
  // ============================================
  const formatTime = useCallback((time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHours = h % 12 || 12;
    return `${displayHours}:${minutes} ${ampm}`;
  }, []);

  const isFrequencyActive = useCallback((frequency) => {
    return alertConfig.frequency === frequency && alertConfig.enabled;
  }, [alertConfig.frequency, alertConfig.enabled]);

  // ============================================
  // RENDER
  // ============================================
  
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex flex-col items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mb-3" />
          <p className="text-sm text-slate-500">Loading alert settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-slate-200 bg-slate-50/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <FiBell className="text-indigo-600" />
              Absent Alert Settings
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Configure how and when absent alerts are sent to parents
            </p>
          </div>
          
          {/* Master System Toggle */}
          <div className="flex items-center gap-3 bg-white px-3 py-1.5 rounded-lg border border-slate-200">
            <span className={`text-xs font-medium ${alertConfig.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
              {alertConfig.enabled ? 'System On' : 'System Off'}
            </span>
            <button
              onClick={handleToggleMaster}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                alertConfig.enabled ? 'bg-emerald-600' : 'bg-slate-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-shadow ${
                alertConfig.enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Alert Message */}
      {alertMessage.show && (
        <div className={`px-4 sm:px-6 py-3 border-b ${
          alertMessage.type === 'success' ? 'bg-emerald-50 border-emerald-200' :
          alertMessage.type === 'error' ? 'bg-rose-50 border-rose-200' :
          'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center gap-2 text-sm">
            {alertMessage.type === 'success' && <FiCheckCircle className="text-emerald-600" />}
            {alertMessage.type === 'error' && <FiAlertCircle className="text-rose-600" />}
            {alertMessage.type === 'info' && <FiInfo className="text-blue-600" />}
            <span className={`text-xs font-medium ${
              alertMessage.type === 'success' ? 'text-emerald-700' :
              alertMessage.type === 'error' ? 'text-rose-700' :
              'text-blue-700'
            }`}>
              {alertMessage.message}
            </span>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && alertConfig.enabled && (
        <div className="px-4 sm:px-6 py-3 bg-rose-50 border-b border-rose-200">
          <div className="flex items-start gap-2">
            <FiAlertCircle className="text-rose-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-bold text-rose-700">Please fix the following:</p>
              <ul className="mt-1 space-y-0.5">
                {validationErrors.map((error, index) => (
                  <li key={index} className="text-xs text-rose-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-rose-600 rounded-full inline-block" />
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4 sm:p-6 space-y-6">
        {/* Alert is disabled - Show message */}
        {!alertConfig.enabled && (
          <div className="text-center py-8">
            <div className="p-4 bg-slate-50 rounded-full w-fit mx-auto mb-4">
              <FiBell className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-sm font-bold text-slate-700">Alert System is Disabled</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
              Toggle the "System On" switch in the header to enable absent alerts.
              Or use the master toggles below to enable a specific alert mode.
            </p>
          </div>
        )}

        {/* Alert is enabled - Show all configuration options */}
        {alertConfig.enabled && (
          <>
            {/* ============================================
                FREQUENCY MODE MASTER TOGGLES
                ============================================ */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-slate-200">
                <h3 className="text-xs font-bold text-slate-700 flex items-center gap-2">
                  <FiPower className="text-indigo-600" />
                  Alert Mode - Master Controls
                </h3>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  Toggle each mode ON/OFF. Enabling a mode will automatically activate email & SMS.
                </p>
              </div>
              
              <div className="p-4 space-y-3">
                {/* Every Class Mode Toggle */}
                <div className={`p-3 rounded-lg border-2 transition-all ${
                  isFrequencyActive('every_class')
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 bg-white'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        isFrequencyActive('every_class')
                          ? 'bg-emerald-100 text-emerald-600'
                          : 'bg-slate-100 text-slate-400'
                      }`}>
                        <FiClock className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`text-xs font-bold ${
                            isFrequencyActive('every_class')
                              ? 'text-emerald-700'
                              : 'text-slate-700'
                          }`}>
                            Every Class
                          </p>
                          {isFrequencyActive('every_class') && (
                            <span className="text-[8px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500">
                          Send alert for each missed class • Real-time
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleToggleEveryClass}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors flex-shrink-0 ${
                        isFrequencyActive('every_class')
                          ? 'bg-emerald-600'
                          : 'bg-slate-300'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                        isFrequencyActive('every_class')
                          ? 'translate-x-8'
                          : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  {isFrequencyActive('every_class') && (
                    <div className="mt-2 pt-2 border-t border-emerald-200 text-[10px] text-emerald-600 flex items-center gap-2">
                      <FiCheckCircle className="w-3 h-3" />
                      Email and SMS alerts will be sent for each missed class
                    </div>
                  )}
                </div>

                {/* Once a Day Mode Toggle */}
                <div className={`p-3 rounded-lg border-2 transition-all ${
                  isFrequencyActive('once_a_day')
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-slate-200 bg-white'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        isFrequencyActive('once_a_day')
                          ? 'bg-indigo-100 text-indigo-600'
                          : 'bg-slate-100 text-slate-400'
                      }`}>
                        <FiClock className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={`text-xs font-bold ${
                            isFrequencyActive('once_a_day')
                              ? 'text-indigo-700'
                              : 'text-slate-700'
                          }`}>
                            Once a Day
                          </p>
                          {isFrequencyActive('once_a_day') && (
                            <span className="text-[8px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500">
                          Send one summary alert per day • Time window controlled
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleToggleOnceADay}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors flex-shrink-0 ${
                        isFrequencyActive('once_a_day')
                          ? 'bg-indigo-600'
                          : 'bg-slate-300'
                      }`}
                    >
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                        isFrequencyActive('once_a_day')
                          ? 'translate-x-8'
                          : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  
                  {isFrequencyActive('once_a_day') && (
                    <div className="mt-2 pt-2 border-t border-indigo-200 text-[10px] text-indigo-600 flex items-center gap-2">
                      <FiCheckCircle className="w-3 h-3" />
                      Email and SMS alerts will be sent once during the configured time window
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ============================================
                INDEPENDENT CHANNEL TOGGLES SECTION
                ============================================ */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('channels')}
                className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <FiSettings className="text-indigo-600" />
                  <span className="text-xs font-bold text-slate-700">Individual Channel Controls</span>
                  <div className="flex gap-1">
                    {alertConfig.channels.email.enabled && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full flex items-center gap-0.5">
                        <FiMail className="w-2.5 h-2.5" /> Email
                      </span>
                    )}
                    {alertConfig.channels.sms.enabled && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-0.5">
                        <FiMessageSquare className="w-2.5 h-2.5" /> SMS
                      </span>
                    )}
                    {!alertConfig.channels.email.enabled && !alertConfig.channels.sms.enabled && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full">
                        No channels active
                      </span>
                    )}
                  </div>
                </div>
                {expandedSections.channels ? <FiChevronUp className="text-slate-400" /> : <FiChevronDown className="text-slate-400" />}
              </button>
              
              {expandedSections.channels && (
                <div className="p-4 space-y-4">
                  <p className="text-[10px] text-slate-500">
                    Fine-tune your alerts by independently enabling/disabling channels.
                  </p>

                  {/* Email Channel */}
                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          alertConfig.channels.email.enabled
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-slate-50 text-slate-400'
                        }`}>
                          <FiMail className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800">Email</p>
                          <p className="text-xs text-slate-500">
                            {alertConfig.channels.email.enabled ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleChannel('email', !alertConfig.channels.email.enabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertConfig.channels.email.enabled ? 'bg-blue-600' : 'bg-slate-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          alertConfig.channels.email.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {/* Email Time Window - Only when Once a Day is active */}
                    {alertConfig.frequency === 'once_a_day' && alertConfig.channels.email.enabled && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <FiClock className="w-4 h-4 text-slate-400" />
                            <span className="text-xs font-medium text-slate-700">Email Time Window</span>
                          </div>
                          <button
                            onClick={() => handleConfigChange(
                              `channels.email.timeWindow.enabled`,
                              !alertConfig.channels.email.timeWindow.enabled
                            )}
                            className={`text-xs font-medium flex items-center gap-1 ${
                              alertConfig.channels.email.timeWindow.enabled
                                ? 'text-indigo-600'
                                : 'text-slate-400'
                            }`}
                          >
                            {alertConfig.channels.email.timeWindow.enabled ? (
                              <><FiToggleRight className="w-4 h-4" /> Enabled</>
                            ) : (
                              <><FiToggleLeft className="w-4 h-4" /> Disabled</>
                            )}
                          </button>
                        </div>

                        {alertConfig.channels.email.timeWindow.enabled && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] font-medium text-slate-600 mb-1">
                                Start Time
                              </label>
                              <input
                                type="time"
                                value={alertConfig.channels.email.timeWindow.startTime}
                                onChange={(e) => handleConfigChange(
                                  `channels.email.timeWindow.startTime`,
                                  e.target.value
                                )}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                              <p className="text-[9px] text-slate-400 mt-0.5">
                                {formatTime(alertConfig.channels.email.timeWindow.startTime)}
                              </p>
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-slate-600 mb-1">
                                End Time
                              </label>
                              <input
                                type="time"
                                value={alertConfig.channels.email.timeWindow.endTime}
                                onChange={(e) => handleConfigChange(
                                  `channels.email.timeWindow.endTime`,
                                  e.target.value
                                )}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                              <p className="text-[9px] text-slate-400 mt-0.5">
                                {formatTime(alertConfig.channels.email.timeWindow.endTime)}
                              </p>
                            </div>
                          </div>
                        )}

                        {!alertConfig.channels.email.timeWindow.enabled && (
                          <p className="text-[10px] text-slate-400 italic">
                            Email alerts will be sent at the end of the day
                          </p>
                        )}
                      </div>
                    )}

                    {/* Note for Every Class frequency */}
                    {alertConfig.frequency === 'every_class' && alertConfig.channels.email.enabled && (
                      <p className="mt-3 text-[10px] text-slate-400 italic flex items-center gap-1">
                        <FiInfo className="w-3 h-3" />
                        Email alerts will be sent immediately for each missed class
                      </p>
                    )}

                    {!alertConfig.channels.email.enabled && (
                      <p className="mt-3 text-[10px] text-slate-400 italic flex items-center gap-1">
                        <FiInfo className="w-3 h-3" />
                        Email channel is disabled. No email alerts will be sent.
                      </p>
                    )}
                  </div>

                  {/* SMS Channel */}
                  <div className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          alertConfig.channels.sms.enabled
                            ? 'bg-purple-50 text-purple-600'
                            : 'bg-slate-50 text-slate-400'
                        }`}>
                          <FiMessageSquare className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-800">SMS</p>
                          <p className="text-xs text-slate-500">
                            {alertConfig.channels.sms.enabled ? 'Active' : 'Inactive'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleChannel('sms', !alertConfig.channels.sms.enabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertConfig.channels.sms.enabled ? 'bg-purple-600' : 'bg-slate-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          alertConfig.channels.sms.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {/* SMS Time Window - Only when Once a Day is active */}
                    {alertConfig.frequency === 'once_a_day' && alertConfig.channels.sms.enabled && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <FiClock className="w-4 h-4 text-slate-400" />
                            <span className="text-xs font-medium text-slate-700">SMS Time Window</span>
                          </div>
                          <button
                            onClick={() => handleConfigChange(
                              `channels.sms.timeWindow.enabled`,
                              !alertConfig.channels.sms.timeWindow.enabled
                            )}
                            className={`text-xs font-medium flex items-center gap-1 ${
                              alertConfig.channels.sms.timeWindow.enabled
                                ? 'text-indigo-600'
                                : 'text-slate-400'
                            }`}
                          >
                            {alertConfig.channels.sms.timeWindow.enabled ? (
                              <><FiToggleRight className="w-4 h-4" /> Enabled</>
                            ) : (
                              <><FiToggleLeft className="w-4 h-4" /> Disabled</>
                            )}
                          </button>
                        </div>

                        {alertConfig.channels.sms.timeWindow.enabled && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] font-medium text-slate-600 mb-1">
                                Start Time
                              </label>
                              <input
                                type="time"
                                value={alertConfig.channels.sms.timeWindow.startTime}
                                onChange={(e) => handleConfigChange(
                                  `channels.sms.timeWindow.startTime`,
                                  e.target.value
                                )}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                              <p className="text-[9px] text-slate-400 mt-0.5">
                                {formatTime(alertConfig.channels.sms.timeWindow.startTime)}
                              </p>
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-slate-600 mb-1">
                                End Time
                              </label>
                              <input
                                type="time"
                                value={alertConfig.channels.sms.timeWindow.endTime}
                                onChange={(e) => handleConfigChange(
                                  `channels.sms.timeWindow.endTime`,
                                  e.target.value
                                )}
                                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              />
                              <p className="text-[9px] text-slate-400 mt-0.5">
                                {formatTime(alertConfig.channels.sms.timeWindow.endTime)}
                              </p>
                            </div>
                          </div>
                        )}

                        {!alertConfig.channels.sms.timeWindow.enabled && (
                          <p className="text-[10px] text-slate-400 italic">
                            SMS alerts will be sent at the end of the day
                          </p>
                        )}
                      </div>
                    )}

                    {/* Note for Every Class frequency */}
                    {alertConfig.frequency === 'every_class' && alertConfig.channels.sms.enabled && (
                      <p className="mt-3 text-[10px] text-slate-400 italic flex items-center gap-1">
                        <FiInfo className="w-3 h-3" />
                        SMS alerts will be sent immediately for each missed class
                      </p>
                    )}

                    {!alertConfig.channels.sms.enabled && (
                      <p className="mt-3 text-[10px] text-slate-400 italic flex items-center gap-1">
                        <FiInfo className="w-3 h-3" />
                        SMS channel is disabled. No SMS alerts will be sent.
                      </p>
                    )}
                  </div>

                  {/* Summary of channel status */}
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <p className="text-[10px] font-medium text-slate-600">Channel Status Summary</p>
                    <div className="flex flex-wrap gap-4 mt-1.5">
                      <span className={`text-[10px] flex items-center gap-1 ${
                        alertConfig.channels.email.enabled ? 'text-blue-600' : 'text-slate-400'
                      }`}>
                        <FiMail className="w-3 h-3" />
                        Email: {alertConfig.channels.email.enabled ? '✅ Active' : '❌ Inactive'}
                      </span>
                      <span className={`text-[10px] flex items-center gap-1 ${
                        alertConfig.channels.sms.enabled ? 'text-purple-600' : 'text-slate-400'
                      }`}>
                        <FiMessageSquare className="w-3 h-3" />
                        SMS: {alertConfig.channels.sms.enabled ? '✅ Active' : '❌ Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ============================================
                SAVE BUTTON SECTION
                ============================================ */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200">
              {hasChanges && (
                <button
                  onClick={handleReset}
                  className="w-full sm:w-auto px-4 py-2 text-xs font-bold text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors flex items-center justify-center gap-1"
                >
                  <FiX className="w-4 h-4" />
                  Discard Changes
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={`w-full sm:w-auto px-6 py-2 text-xs font-bold text-white rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  saving || !hasChanges
                    ? 'bg-slate-300 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                <FiSave className="w-4 h-4" />
                {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AlertSettings;